import type { WalletBalanceOriginRow } from './types'

const toMajorNumber = (
  value: string | number | null | undefined
): number | null => {
  if (value == null) return null
  if (typeof value === 'string' && !value.trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const mapPiDisplayId = (raw: CheckingAccountOriginResponse): string | null => {
  if (raw.pi_sequential_id != null) return String(raw.pi_sequential_id)
  const piId = raw.pi_id?.trim()
  return piId || null
}

const isExpiredPiOrigin = (raw: CheckingAccountOriginResponse): boolean => {
  if (raw.type !== 'pi') return false
  if (typeof raw.expired === 'boolean') return raw.expired
  if (!raw.end_at) return false
  const expiresAt = Date.parse(raw.end_at)
  return Number.isFinite(expiresAt) && expiresAt < Date.now()
}

export const mapCheckingAccountOrigins = (
  origins?: CheckingAccountOriginResponse[] | null
): WalletBalanceOriginRow[] => {
  if (!origins?.length) return []

  const rows: WalletBalanceOriginRow[] = []

  for (const raw of origins) {
    const checkingAccountId = raw.checking_account_id?.trim()
    const total = toMajorNumber(raw.total_value)
    const consumed = toMajorNumber(raw.consumed)
    const available = toMajorNumber(raw.available ?? raw.balance)
    if (
      !checkingAccountId ||
      total == null ||
      consumed == null ||
      available == null
    ) {
      continue
    }

    if (raw.type === 'default') {
      rows.push({
        kind: 'preexisting',
        checkingAccountId,
        total,
        consumed,
        available,
        expiresAt: null,
        expired: false
      })
      continue
    }

    if (raw.type === 'pi') {
      const id = mapPiDisplayId(raw) ?? checkingAccountId
      const name = raw.pi_name?.trim() || id

      rows.push({
        kind: 'pi',
        checkingAccountId,
        id,
        name,
        total,
        consumed,
        available,
        expiresAt: raw.end_at ?? null,
        expired: isExpiredPiOrigin(raw)
      })
    }
  }

  return rows
}

export const buildOriginByIdMap = (origins: WalletBalanceOriginRow[]) =>
  new Map(origins.map(origin => [origin.checkingAccountId, origin]))
